import React, { useContext, useEffect, useState } from "react";
import {
	DefaultErrorMessage,
	FormKeys,
	UppyFileMetaData,
	ZomentumEntities,
	ZomentumRoutes
} from "@/Contracts";
import {
	AddSalesActivityCommentRequest,
	SalesActivityCommentFormValues,
	ESalesActivityType,
	SalesActivity
} from "@/Contracts/Others/SalesActivity";
import SalesActivityActionCreators, {
	EditCommentSuccessfulAction
} from "../../../../store/actions/SalesActivity";
import { Attachment } from "@/Contracts/Others/RecentActivityFeed";
import { UploadResult } from "@uppy/core";
import { extension } from "mime-types";
import { getFileExtensionFromUrl, getFileName } from "@/Utils";
import { downloadAttachment } from "@/Utils/Service";
import DownloadFile from "downloadjs";
import {
	SalesActivityModuleState,
	useSalesActivityModuleDispatch,
	useSalesActivityModuleSelector
} from "../../../../store";
import { useTestEnvironment } from "@/Hooks";
import ZMessage from "@molecules/ZMessage";
import ZCommentDrawer from "../../../../templates/ZDrawers/ZSalesActivities/ZComment";
import { IAddSalesActivityDrawerProps } from "../interface";
import Form from "antd/lib/form";
import SalesActivityAddWrapper from "../Wrappers/AddWrapper";
import SalesActivityEditWrapper from "../Wrappers/EditWrapper";
import {
	ICommentUIProps,
	IOnlyCommentUIProps
} from "../../../../templates/ZDrawers/ZSalesActivities/interface";
import ZFileUpload from "@molecules/ZFileUpload";
import { UppyAllowedFileTypes as AllowedFileTypes } from "@molecules/ZFileUpload/interface";
import { ParentContext } from "../../../../store/context/ParentContext";
import { isVendorApp } from "@/Utils";
import ZModalConfirm from "@atoms/ZModalConfirmation";
import { getAppPathname } from "@/Utils/routing/getAppPathname";

const Comment: React.FC<IAddSalesActivityDrawerProps> = ({
	visible = false,
	isEditMode = false,
	onClose,
	onSubmit
}) => {
	const [isCrossAssign, setIsCrossAssign] = useState(false);
	const parentContext = useContext(ParentContext);
	const { currentActiveSalesActivity } = useSalesActivityModuleSelector(
		(state: SalesActivityModuleState) => {
			return {
				currentActiveSalesActivity:
					state.salesActivity.currentActiveSalesActivity
			};
		}
	);
	const { userData } = parentContext.user;
	const { currentActivePartner } = parentContext.vendorPartner;
	const { currentActiveOpportunity } = parentContext.opportunity;
	const { currentActiveVendorClient } = parentContext.vendorClient;
	const { isFormEdited } = parentContext.setting;

	const [form] = Form.useForm();
	const dispatch = useSalesActivityModuleDispatch();

	const [attachments, setAttachments] = useState<Attachment[]>([]);
	const [isUploadAttachmentsModalOpen, setIsUploadAttachmentsModalOpen] =
		useState<boolean>(false);

	useEffect(() => {
		if (visible) {
			if (
				isEditMode &&
				currentActiveSalesActivity &&
				currentActiveSalesActivity.feed_details
			) {
				const feed = currentActiveSalesActivity.feed_details;
				setAttachments(feed.attachments ?? []);
				form.setFieldsValue({
					body: feed.body,
					relation:
						currentActiveSalesActivity?.feed_details.entity_type,
					pinned: feed.pinned,
					author_id: feed.author_id
				});
				setIsCrossAssign(checkIfCrossAssign(feed.author_id));
			}
		} else {
			setAttachments([]);
			setIsUploadAttachmentsModalOpen(false);
			setIsCrossAssign(false);
		}
	}, [visible, currentActiveSalesActivity]);

	const handleClose = () => {
		form.resetFields();
		setAttachments([]);
		setIsUploadAttachmentsModalOpen(false);
		onClose && onClose();
		parentContext.setting.resetFormEdited?.(FormKeys.AddEditNote);
	};

	const closeNoteDrawer = () => {
		if (!isFormEdited?.[FormKeys.AddEditNote]) {
			handleClose();
		} else {
			ZModalConfirm({
				content: `Are you sure you want to discard ${
					isEditMode ? "editing" : "adding"
				} note?`,
				onOk: handleClose
			});
		}
	};

	const atPartnerDetailPage = (): boolean => {
		return (
			!!currentActivePartner &&
			(getAppPathname() ===
				ZomentumRoutes.VendorPartnersDetail.replace(
					":id",
					currentActivePartner.id
				) ||
				getAppPathname() === ZomentumRoutes.VendorPartnerList)
		);
	};

	const atClientDetailPage = (): boolean => {
		return (
			!!currentActiveVendorClient &&
			(getAppPathname() ===
				ZomentumRoutes.VendorClientDetail.replace(
					":id",
					currentActiveVendorClient.id
				) ||
				getAppPathname() === ZomentumRoutes.VendorClientList)
		);
	};

	const getFormValues = (
		addCommentFormValues: SalesActivityCommentFormValues
	) => {
		if (isVendorApp()) {
			const addCommentRequest = new AddSalesActivityCommentRequest();
			addCommentRequest.body = addCommentFormValues.body;
			addCommentRequest.pinned = addCommentFormValues.pinned
				? addCommentFormValues.pinned
				: false;
			const isPartnerSelected = atPartnerDetailPage();
			const isClientSelected = atClientDetailPage();
			addCommentRequest.entity_type = isPartnerSelected
				? ZomentumEntities.VendorCompany
				: isClientSelected
				? ZomentumEntities.VendorClient
				: ZomentumEntities.VendorOpportunity;
			if (isPartnerSelected && currentActivePartner) {
				addCommentRequest.entity_id = currentActivePartner.id;
			} else if (isClientSelected && currentActiveVendorClient) {
				addCommentRequest.entity_id = currentActiveVendorClient.id;
			} else {
				currentActiveOpportunity &&
					(addCommentRequest.entity_id = currentActiveOpportunity.id);
			}
			addCommentRequest.author_id =
				addCommentFormValues?.author_id ?? userData?.id;
			addCommentRequest.attachments = attachments;
			return addCommentRequest;
		} else {
			const addCommentRequest = new AddSalesActivityCommentRequest();
			addCommentRequest.body = addCommentFormValues.body;
			addCommentRequest.pinned = addCommentFormValues.pinned
				? addCommentFormValues.pinned
				: false;
			addCommentRequest.entity_type = addCommentFormValues.relation;
			if (
				addCommentFormValues.relation === ZomentumEntities.Opportunity
			) {
				addCommentRequest.entity_id = addCommentFormValues.opportunity;
			} else if (
				addCommentFormValues.relation === ZomentumEntities.Client
			) {
				addCommentRequest.entity_id =
					addCommentFormValues.client_company;
			} else if (
				addCommentFormValues.relation === ZomentumEntities.VendorCompany
			) {
				addCommentRequest.entity_id = addCommentFormValues.company;
			} else if (
				addCommentFormValues.relation === ZomentumEntities.VendorClient
			) {
				addCommentRequest.entity_id = addCommentFormValues.company;
			}
			addCommentRequest.author_id =
				addCommentFormValues.author_id ?? userData?.id;
			addCommentRequest.attachments = attachments;
			return addCommentRequest;
		}
	};

	const addComment = async (
		addCommentFormValues: SalesActivityCommentFormValues
	) => {
		dispatch(SalesActivityActionCreators.showSalesActivityLoader());
		try {
			const addCommentRequest = getFormValues(addCommentFormValues);
			const addCommentResponse = await dispatch(
				SalesActivityActionCreators.addComment(
					addCommentRequest,
					isCrossAssign
				)
			);
			if (onSubmit) {
				onSubmit(addCommentResponse.addComment);
			}
			handleClose();
			ZMessage.success("Note added successfully");
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const editComment = async (
		editCommentFormValues: SalesActivityCommentFormValues
	) => {
		dispatch(SalesActivityActionCreators.showSalesActivityLoader());
		try {
			if (
				currentActiveSalesActivity &&
				currentActiveSalesActivity.feed_details
			) {
				const editCommentRequest: SalesActivity = {
					...currentActiveSalesActivity
				};
				const formValues = getFormValues(editCommentFormValues);
				editCommentRequest.feed_details = {
					...currentActiveSalesActivity.feed_details,
					...formValues
				};
				const editCommentResponse = await dispatch(
					SalesActivityActionCreators.editComment(editCommentRequest)
				);
				if (onSubmit) {
					onSubmit(
						(
							editCommentResponse as unknown as EditCommentSuccessfulAction
						).editComment,
						form.getFieldValue(ZomentumEntities.Opportunity)
					);
				}
				handleClose();
				ZMessage.success("Note edited successfully");
			} else {
				ZMessage.error(DefaultErrorMessage);
			}
		} catch (error) {
			console.error(error);
		} finally {
			dispatch(SalesActivityActionCreators.hideSalesActivityLoader());
		}
	};

	const openUploadAttachmentModal = () => {
		setIsUploadAttachmentsModalOpen(true);
	};
	const closeUploadAttachmentModal = () => {
		setIsUploadAttachmentsModalOpen(false);
	};

	const addUploadedAttachment = (result: UploadResult<UppyFileMetaData>) => {
		if (result.successful.length > 0) {
			const files: Attachment[] = result.successful.map(file => ({
				file_name: file.name,
				path: file.uploadURL
			}));
			setAttachments(prevState => [...prevState.concat(files)]);
		}
		closeUploadAttachmentModal();
	};

	const removeAttachment = (selectedFile: Attachment) => {
		const files = attachments.filter(
			file => file.path !== selectedFile.path
		);
		setAttachments(files);
	};

	const downloadFile = async (attachment: Attachment) => {
		const blobData = await downloadAttachment(attachment.path);
		const extFromBlob = extension(blobData.type);
		const ext =
			extFromBlob && extFromBlob !== "bin" && extFromBlob !== "mpga"
				? extFromBlob
				: getFileExtensionFromUrl(attachment.file_name) ??
				  getFileExtensionFromUrl(attachment.path);
		DownloadFile(blobData, `${getFileName(attachment)}.${ext}`);
	};

	const isTestingEnvironment = useTestEnvironment();

	const handleFormChange = () => {
		!isFormEdited?.[FormKeys.AddEditNote] &&
			parentContext.setting.setFormEdited?.(FormKeys.AddEditNote);
	};

	const handleOwnerChange = (value: string) => {
		setIsCrossAssign(checkIfCrossAssign(value));
	};

	const checkIfCrossAssign = (selectedUserId: string) => {
		const user = parentContext.user.companyUserList.find(
			user => user.id === selectedUserId
		);
		if (!user) {
			return true;
		} else {
			return false;
		}
	};

	const isEditable = !!currentActiveSalesActivity?.feed_details
		?.sharing_details
		? currentActiveSalesActivity.feed_details.sharing_details.is_editable
		: true;

	const addCommentUIProps: IOnlyCommentUIProps = {
		onSubmit: isEditMode ? editComment : addComment,
		downloadFile,
		removeAttachment,
		openUploadAttachmentModal,
		attachments,
		handleFormChange,
		handleOwnerChange,
		isReadOnly: !isEditable,
		isEditMode
	};

	return (
		<>
			{isEditMode ? (
				<SalesActivityEditWrapper<ICommentUIProps, IOnlyCommentUIProps>
					visible={visible}
					onClose={closeNoteDrawer}
					Component={ZCommentDrawer}
					form={form}
					formUIProps={addCommentUIProps}
					salesActivityType={ESalesActivityType.COMMENT}
				/>
			) : (
				<SalesActivityAddWrapper<ICommentUIProps, IOnlyCommentUIProps>
					visible={visible}
					onClose={closeNoteDrawer}
					Component={ZCommentDrawer}
					form={form}
					formUIProps={addCommentUIProps}
					salesActivityType={ESalesActivityType.COMMENT}
				/>
			)}
			{!isTestingEnvironment && (
				<ZFileUpload
					useModal={{
						visible: isUploadAttachmentsModalOpen,
						title: "Upload attachments"
					}}
					useURL
					maxNumberOfFiles={5}
					minNumberOfFiles={1}
					allowedFileTypes={[AllowedFileTypes.ALL]}
					maxFileSize={26214400}
					allowMultipleUploads={true}
					onCancel={closeUploadAttachmentModal}
					onUploadSuccess={addUploadedAttachment}
				/>
			)}
		</>
	);
};

export default Comment;
